import type { JSONContent } from '@tiptap/react';
import { isEqual } from 'lodash';

import type { CouponBannerDto } from '@/lib/dtos';

import { TEXT_ON_DARK, normalizeHex, pickTextColor } from './color-contrast';

export type CouponPosition = 'top' | 'bottom';
export type TextColorMode = 'auto' | 'manual';

export interface CouponBannerForm {
  isActive: boolean;
  bannerText: JSONContent;
  couponCode: string;
  backgroundColorHex: string;
  foregroundColorHex: string;
  /** Front-only; not sent to the API. */
  textColorMode: TextColorMode;
  position: CouponPosition;
  animated: boolean;
}

export const COUPON_TEXT_MIN = 5;
export const COUPON_TEXT_MAX = 70;

const DEFAULT_BACKGROUND = '#19304D';

export const DEFAULT_COUPON_FORM: CouponBannerForm = {
  isActive: false,
  bannerText: { type: 'doc', content: [] },
  couponCode: '',
  backgroundColorHex: DEFAULT_BACKGROUND,
  foregroundColorHex: TEXT_ON_DARK,
  textColorMode: 'auto',
  position: 'top',
  animated: false,
};

function parseBannerText(raw: string): JSONContent {
  if (raw === '') return DEFAULT_COUPON_FORM.bannerText;
  try {
    const parsed: JSONContent | null = JSON.parse(raw);
    return typeof parsed === 'object' && parsed !== null
      ? parsed
      : DEFAULT_COUPON_FORM.bannerText;
  } catch {
    return DEFAULT_COUPON_FORM.bannerText;
  }
}

export function couponDtoToForm(dto: CouponBannerDto | null): CouponBannerForm {
  if (!dto) return DEFAULT_COUPON_FORM;
  const background = normalizeHex(dto.backgroundColorHex) ?? DEFAULT_BACKGROUND;
  const foreground = normalizeHex(dto.foregroundColorHex) ?? TEXT_ON_DARK;
  return {
    isActive: dto.isActive,
    bannerText: parseBannerText(dto.bannerText),
    couponCode: dto.couponCode,
    backgroundColorHex: background,
    foregroundColorHex: foreground,
    textColorMode: foreground === pickTextColor(background) ? 'auto' : 'manual',
    position: dto.position,
    animated: dto.animated,
  };
}

export function couponFormToDto(form: CouponBannerForm): CouponBannerDto {
  return {
    isActive: form.isActive,
    bannerText: JSON.stringify(form.bannerText),
    couponCode: form.couponCode,
    backgroundColorHex: form.backgroundColorHex,
    foregroundColorHex: form.foregroundColorHex,
    position: form.position,
    animated: form.animated,
  };
}

export function getBannerPlainText(content: JSONContent): string {
  const own = content.text ?? '';
  const children = (content.content ?? []).map(getBannerPlainText).join('');
  return own + children;
}

export type CouponFieldError =
  | 'textTooShort'
  | 'textTooLong'
  | 'couponRequired';

export function validateCouponForm(
  form: CouponBannerForm
): Partial<Record<'text' | 'couponCode', CouponFieldError>> {
  const errors: Partial<Record<'text' | 'couponCode', CouponFieldError>> = {};
  const length = getBannerPlainText(form.bannerText).length;
  if (length < COUPON_TEXT_MIN) {
    errors.text = 'textTooShort';
  } else if (length > COUPON_TEXT_MAX) {
    errors.text = 'textTooLong';
  }
  if (form.couponCode.trim() === '') {
    errors.couponCode = 'couponRequired';
  }
  return errors;
}

/** The rich text editor omits `content` for an empty text; the DTO keeps `[]`. */
function withoutEmptyContent(node: JSONContent): JSONContent {
  const { content, ...rest } = node;
  return content && content.length > 0
    ? { ...rest, content: content.map(withoutEmptyContent) }
    : rest;
}

export function isCouponFormDirty(
  baseline: CouponBannerForm,
  form: CouponBannerForm
): boolean {
  const comparable = (value: CouponBannerForm) => ({
    ...value,
    textColorMode: undefined,
    bannerText: withoutEmptyContent(value.bannerText),
  });
  return !isEqual(comparable(baseline), comparable(form));
}

export function applyBackground(
  form: CouponBannerForm,
  hex: string
): CouponBannerForm {
  return {
    ...form,
    backgroundColorHex: hex,
    foregroundColorHex:
      form.textColorMode === 'auto'
        ? pickTextColor(hex)
        : form.foregroundColorHex,
  };
}
